'use client';

import { useState, useEffect } from 'react';
import Image from 'next/image';
import Link from '@eliadmin/core/src/components/link';

import type { TDynamicData } from '@eliadmin/core/src/api/dynamics/dynamics-api';
import type { SectionProps } from './custom-homepage';

import { cn } from '@eliadmin/core/src/utils/style-utils';
import { Heading } from '@eliadmin/core/src/components/ui/heading';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useBreakpoint } from '@eliadmin/core/src/hooks/use-breakpoint';
import { Paragraph } from '@eliadmin/core/src/components/ui/paragraph';

const DURATION = 3000;

function ProgressBar({ active }: { active: boolean }) {
  const [width, setWidth] = useState('0%');

  useEffect(() => {
    if (active) {
      const timer = setTimeout(() => setWidth('100%'), 10);
      return () => clearTimeout(timer);
    } else {
      setWidth('0%');
    }
  }, [active]);

  return (
    <div
      className="h-[1px] bg-black"
      style={{
        width,
        transition: active ? `width ${DURATION}ms linear` : 'none',
      }}
    />
  );
}

export function Process(_props: { items: TDynamicData[] } & Partial<SectionProps>) {
  const { t } = useTranslations();
  const isDesktop = useBreakpoint('sm');
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    if (!_props.items.length) return;
    const timer = setInterval(() => {
      setActiveIndex(prev => (prev + 1) % _props.items.length);
    }, DURATION);
    return () => clearInterval(timer);
  }, [_props.items.length]);

  return (
    <section className={cn('custom-section', 'py-16 sm:py-20 lg:py-24')}>
      <div
        className={cn(
          'custom-container',
          'flex flex-col items-start justify-start gap-8 md:flex-row md:items-center md:justify-between lg:gap-12',
        )}
      >
        <div className="relative h-[383px] w-full overflow-hidden rounded-[20px] sm:h-[480px] md:h-[520px] md:max-w-[653px] lg:h-[700px]">
          <div className="primary-overlay" />
          <Image
            src={_props.image ?? '/images/placeholder.png'}
            alt={_props?.subtitle ?? ''}
            fill
            className="absolute inset-0 z-0 h-full w-full object-cover object-center"
          />
        </div>
        <div className="flex w-full flex-col items-start justify-start gap-8 sm:gap-10 md:max-w-[629px] lg:gap-12">
          {_props?.title && (
            <Heading
              subtitle={_props.subtitle}
              title={_props.title}
              tracking
              className={{ title: 'text-balance' }}
            />
          )}
          <div className="flex w-full flex-col items-start justify-start gap-6">
            {_props.items.map((process, index) => {
              const isActive = index === activeIndex;
              return (
                <div
                  key={process.name}
                  className={cn(
                    'flex w-full flex-col items-start justify-start gap-3 transition-all',
                    !isActive && 'opacity-50',
                  )}
                >
                  <div className="flex items-center justify-start gap-4 sm:gap-6">
                    <div className="flex max-h-10 min-h-10 min-w-10 max-w-10 items-center justify-center rounded-full bg-secondary">
                      <Heading title={`0${index + 1}`} center tag="h5" as="span" />
                    </div>
                    <Heading title={process.name} tag={isDesktop ? 'h3' : 'h4'} as="h3" />
                  </div>
                  <div
                    className={cn(
                      'w-full overflow-hidden pl-14 transition-all duration-500 ease-in-out sm:pl-16',
                      isActive ? 'max-h-[200px] opacity-100' : 'max-h-0 opacity-0',
                    )}
                  >
                    <Paragraph
                      size={isDesktop ? 'lg' : 'base'}
                      innerHtml={process?.fields[0]?.value}
                    />
                  </div>
                  <div className="flex h-[1px] w-full items-center justify-start bg-border">
                    <ProgressBar active={isActive} />
                  </div>
                </div>
              );
            })}
          </div>
          <Button asChild className="w-full font-bold sm:w-fit" variant={'secondary'}>
            <Link prefetch={false} href={'#price-offer'}>
              {t('text3670')}
            </Link>
          </Button>
        </div>
      </div>
    </section>
  );
}
